import React, { useEffect, useRef, useState } from 'react';
import { fetchWithCorpId, asArray } from '../utils/api';
import { useParams } from 'react-router-dom';
import { Section, Field, FormLoading } from './ui/formkit';
import { useToast } from './ui/Toast';
import { toDateInputValue } from '../utils/dateUtils';

const DEFAULT_INVOICE = {
  number: '', clientId: '', amount: '',
  invoiceDate: '', dueDate: '', status: '', items: [],
};

function InvoiceForm({
  mode = 'create',
  initialData = {},
  invoiceId,
  formRef,
  hideActions = false,
  onSuccess,
  onCancel,
}) {
  const params = useParams();
  const toast = useToast();
  const id = invoiceId ?? params.id;
  const isEdit = mode === 'edit';
  const [invoice, setInvoice] = useState(DEFAULT_INVOICE);
  const [clients, setClients] = useState([]);
  const [message, setMessage] = useState('');
  const [loadingRecord, setLoadingRecord] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  // Snapshot of the last saved state; drives the drawer's discard guard.
  // In edit mode it is set once the record loads.
  const initialSnapshotRef = useRef(isEdit ? null : JSON.stringify(DEFAULT_INVOICE));

  useEffect(() => {
    if (!isEdit || !id) {
      setLoadingRecord(false);
      return;
    }
    let cancelled = false;
    setLoadingRecord(true);
    fetchWithCorpId(`/invoices/${id}`)
      .then((data) => {
        if (cancelled || !data) return;
        const normalized = {
          ...DEFAULT_INVOICE,
          ...data,
          invoiceDate: toDateInputValue(data.invoiceDate),
          dueDate: toDateInputValue(data.dueDate),
          items: data.items || [],
        };
        setInvoice(normalized);
        initialSnapshotRef.current = JSON.stringify(normalized);
      })
      .catch((error) => {
        if (!cancelled) setMessage('Could not load this invoice: ' + error.message);
      })
      .finally(() => {
        if (!cancelled) setLoadingRecord(false);
      });
    return () => { cancelled = true; };
  }, [isEdit, id]);

  useEffect(() => {
    fetchWithCorpId('/clients?page=0&size=99999&sort=name')
      .then((data) => setClients(asArray(data)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      const normalized = {
        ...DEFAULT_INVOICE,
        ...initialData,
        invoiceDate: toDateInputValue(initialData.invoiceDate),
        dueDate: toDateInputValue(initialData.dueDate),
        items: initialData.items || [],
      };
      setInvoice(normalized);
      initialSnapshotRef.current = JSON.stringify(normalized);
    }
  }, [initialData]);

  const isDirty = initialSnapshotRef.current !== null
    && JSON.stringify(invoice) !== initialSnapshotRef.current;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInvoice(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const items = [...(invoice.items || [])];
    items[index] = { ...items[index], [field]: value };
    setInvoice(prev => ({ ...prev, items }));
  };

  const addItem = () => {
    setInvoice(prev => ({ ...prev, items: [...(prev.items || []), { description: '', quantity: 1, price: 0 }] }));
  };

  const removeItem = (index) => {
    setInvoice(prev => ({ ...prev, items: (prev.items || []).filter((_, i) => i !== index) }));
  };

  const calculateAmount = (item) => (Number(item.quantity) || 0) * (Number(item.price) || 0);
  const totalAmount = (invoice.items || []).reduce((sum, item) => sum + calculateAmount(item), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setMessage('');
    setSaving(true);
    try {
      const url = isEdit && invoice.id ? `/invoices/${invoice.id}` : '/invoices';
      const response = await fetchWithCorpId(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...invoice, amount: totalAmount || invoice.amount }),
      });
      if (response) {
        initialSnapshotRef.current = JSON.stringify(invoice);
        toast.success(isEdit ? 'Invoice updated' : 'Invoice created');
        onSuccess?.();
      } else {
        setMessage('Failed to save. Please try again.');
      }
    } catch (error) {
      setMessage(error.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loadingRecord) {
    return <FormLoading label="Loading invoice…" />;
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} data-dirty={isDirty ? 'true' : 'false'}>
      {message && (
        <div className="alert error" role="alert" style={{ marginBottom: '20px' }}>
          {message}
        </div>
      )}

      <Section title="Invoice" description="Identifier and the client being invoiced.">
        <Field label="Invoice number">
          <input type="number" name="number" value={invoice.number || ''} readOnly
            style={{ background: 'var(--surface-soft)', cursor: 'not-allowed', color: 'var(--muted)' }} />
        </Field>
        <Field label="Status">
          <select name="status" value={invoice.status || ''} onChange={handleChange}>
            <option value="">Select status</option>
            <option value="Pending">Pending</option>
            <option value="Sent">Sent</option>
            <option value="Paid">Paid</option>
            <option value="Overdue">Overdue</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </Field>
        <Field label="Client" span={2} required>
          <select name="clientId" value={invoice.clientId || ''} onChange={handleChange} required>
            <option value="">Select client</option>
            {clients.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </Field>
      </Section>

      <Section title="Dates">
        <Field label="Invoice date">
          <input type="date" name="invoiceDate" value={invoice.invoiceDate || ''} onChange={handleChange} />
        </Field>
        <Field label="Due date">
          <input type="date" name="dueDate" value={invoice.dueDate || ''} onChange={handleChange} />
        </Field>
      </Section>

      <Section title="Line items" description="Add the items being billed on this invoice." grid={null}>
        <div className="table-wrapper" style={{ marginBottom: '12px' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Description</th>
                <th style={{ width: '90px' }}>Qty</th>
                <th style={{ width: '120px' }}>Price</th>
                <th style={{ width: '120px' }}>Amount</th>
                <th style={{ width: '44px' }}></th>
              </tr>
            </thead>
            <tbody>
              {(invoice.items || []).length === 0 ? (
                <tr className="state-row"><td colSpan="5">No line items yet.</td></tr>
              ) : (invoice.items || []).map((item, index) => (
                <tr key={index}>
                  <td>
                    <input value={item.description || ''} onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      style={{ width: '100%' }} />
                  </td>
                  <td>
                    <input type="number" min="0" value={item.quantity || ''} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} />
                  </td>
                  <td>
                    <input type="number" min="0" step="0.01" value={item.price || ''} onChange={(e) => handleItemChange(index, 'price', e.target.value)} />
                  </td>
                  <td style={{ fontFamily: 'var(--font-serif)', fontSize: '16px', color: 'var(--ink)' }}>
                    ${calculateAmount(item).toFixed(2)}
                  </td>
                  <td>
                    <button type="button" onClick={() => removeItem(index)}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: '6px' }}
                      title="Remove" aria-label="Remove line item">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
          <button type="button" onClick={addItem} className="btn-secondary">+ Add item</button>
          <div style={{
            fontFamily: 'var(--font-sans)', fontSize: '12px',
            color: 'var(--muted)', letterSpacing: '1.2px', textTransform: 'uppercase',
            display: 'flex', alignItems: 'baseline', gap: '12px',
          }}>
            Total
            <span style={{
              fontFamily: 'var(--font-serif)', fontSize: '24px',
              color: 'var(--ink)', letterSpacing: '-0.4px',
            }}>${totalAmount.toFixed(2)}</span>
          </div>
        </div>
      </Section>

      {!hideActions && (
        <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {onCancel && (
            <button type="button" onClick={onCancel} className="btn-secondary" disabled={saving}>Cancel</button>
          )}
        </div>
      )}
    </form>
  );
}

export default InvoiceForm;
